import { z } from 'zod';

import {
  AdvertisingPackageResponse,
  MediaPlanBooking,
  MediaPlanBookingRequest,
  MediaPlanPackageBooking,
  MediaPlanPackageBookingRequest,
  SpaceResponse,
} from '@/api/data-contracts';

export const SpaceBookingSchema = z.object({
  tempId: z.string(),
  /** The saved plan line this draft continues; absent for a new line and for a package's members. */
  id: z.string().optional(),
  availability: z.object({
    start: z.string(),
    end: z.string(),
  }),
  spaceId: z.string(),
  status: z.string(),
  paymentTerms: z.number().optional(),
  purchasedServices: z.string().array().optional(),
  servicesPrice: z.number().optional(),
  price: z.number().optional(),
  sellingPrice: z.number().optional(),
  isChild: z.boolean().optional(),
});

export const PackageBookingSchema = z.object({
  tempId: z.string(),
  /** The saved package line this draft continues; absent for a new line. */
  id: z.string().optional(),
  availability: z.object({
    start: z.string(),
    end: z.string(),
  }),
  advertisingPackageId: z.string(),
  status: z.string(),
  paymentTerms: z.number().optional(),
  bookings: SpaceBookingSchema.array(),
  purchasedServices: z.string().array().optional(),
  servicesPrice: z.number().optional(),
  price: z.number().optional(),
  sellingPrice: z.number().optional(),
});

type SpaceBookingType = z.infer<typeof SpaceBookingSchema>;
type PackageBookingType = z.infer<typeof PackageBookingSchema>;

type CartAvailability = { start: string; end: string };

export const mapSpaceToSpaceBooking = (
  spaces: (SpaceResponse & { cartAvailability?: CartAvailability; cartPrice?: number })[],
) =>
  spaces.map((space) => {
    const availability = space.cartAvailability ?? space.availability;
    const price = space.cartPrice ?? space.price;

    return {
      tempId: `${space.id}-1`,
      availability: {
        start: !(availability.start as unknown as string | undefined)?.includes('T')
          ? `${availability.start}T00:00:00Z`
          : availability.start,
        end: !(availability.end as unknown as string | undefined)?.includes('T')
          ? `${availability.end}T20:59:59Z`
          : availability.end,
      },
      spaceId: space.id,
      space: { ...space, price },
      status: space.status,
      purchasedServices: [],
      price,
      sellingPrice: price,
      servicesPrice: 0,
    };
  });

export const mapPackageToPackageBooking = (
  packages: (AdvertisingPackageResponse & {
    cartAvailability?: CartAvailability;
    cartPrice?: number;
  })[],
) =>
  packages.map((advertisingPackage) => {
    const availability = advertisingPackage.cartAvailability ?? advertisingPackage.availability;
    const price = advertisingPackage.cartPrice ?? advertisingPackage.price;

    return {
      tempId: `${advertisingPackage.id}-1`,
      availability: {
        start: !(availability.start as unknown as string | undefined)?.includes('T')
          ? `${availability.start}T00:00:00Z`
          : availability.start,
        end: !(availability.end as unknown as string | undefined)?.includes('T')
          ? `${availability.end}T20:59:59Z`
          : availability.end,
      },
      advertisingPackageId: advertisingPackage.id,
      advertisingPackage: { ...advertisingPackage, price },
      status: advertisingPackage.status,
      purchasedServices: [],
      price,
      sellingPrice: price,
      servicesPrice: 0,
      bookings: mapSpaceToSpaceBooking(advertisingPackage.spaces || []),
    };
  });

export const mapPlanSpaceBookingsToRequest = (bookings: MediaPlanBooking[], isChild?: boolean) =>
  bookings.map((booking: MediaPlanBooking) => ({
    tempId: booking.id,
    // A package's members are rebuilt on every read and keep no id of their own.
    id: isChild ? undefined : booking.id,
    availability: {
      start: !booking.availability.start?.includes('T')
        ? `${booking.availability.start}T00:00:00Z`
        : booking.availability.start,
      end: !booking.availability.end?.includes('T')
        ? `${booking.availability.end}T20:59:59Z`
        : booking.availability.end,
    },
    spaceId: booking.space.id,
    space: booking.space,
    status: booking.status,
    paymentTerms: booking.paymentTerms,
    purchasedServices: booking.purchasedServices,
    price: booking.price,
    sellingPrice: booking.sellingPrice,
    servicesPrice: booking.servicesPrice,
    // The isChild key must stay present even when undefined: the paymentTerms
    // cells gate on `'isChild' in row`, so hydrated standalone spaces show the
    // '0 days' text instead of the Select. Pinned legacy quirk.
    isChild,
  }));

export const mapPlanPackageBookingsToRequest = (bookings: MediaPlanPackageBooking[]) =>
  bookings.map((pb: MediaPlanPackageBooking) => ({
    tempId: pb.id,
    id: pb.id,
    availability: {
      start: !pb.availability.start?.includes('T')
        ? `${pb.availability.start}T00:00:00Z`
        : pb.availability.start,
      end: !pb.availability.end?.includes('T')
        ? `${pb.availability.end}T20:59:59Z`
        : pb.availability.end,
    },
    advertisingPackageId: pb.advertisingPackage.id,
    advertisingPackage: pb.advertisingPackage,
    paymentTerms: pb.paymentTerms,
    status: pb.status,
    purchasedServices: pb.purchasedServices ?? [],
    price: pb.price,
    sellingPrice: pb.sellingPrice,
    servicesPrice: pb.servicesPrice,
    bookings: mapPlanSpaceBookingsToRequest(pb.bookings, true),
  }));

/**
 * Rename the applied-filters entries to the keys the backend actually reads
 * when a media plan is created (`POST /mediaPlans`) or edited (`PATCH /mediaPlans/{id}`).
 *
 * `MediaPlan.filters` is contractually a free-form map
 * (`{ type: object, additionalProperties: { type: object } }`) and the backend
 * persists whatever it receives VERBATIM. But `GET /mediaPlans/{planId}/filters`
 * projects that stored map into `MediaPlanAppliedFilters` by reading the
 * `spaces` and `packages` keys only. Writing the DTO's own field names —
 * `spaceFilters` / `packageFilters` — into the map therefore stores them
 * somewhere nothing ever reads again, and the plan comes back with
 * `{"spaceFilters":null,"packageFilters":null}`: filters chosen while creating
 * a plan silently disappear.
 *
 * `PUT /mediaPlans/{planId}/filters` accepts the `MediaPlanAppliedFilters` DTO
 * and performs this same rename server-side, which is why that path always
 * worked. This mapper applies it to the create/update payload.
 *
 * Entries already stored under the backend keys (which is what
 * `useMediaPlanFormHydration` puts in the form when editing an existing plan,
 * since it seeds `request.filters` from the raw `plan.filters` map) are kept,
 * but a freshly chosen `spaceFilters` / `packageFilters` always wins over the
 * hydrated value — otherwise the stale one would shadow the user's edit.
 * Any other key is passed through untouched.
 */
export const mapFormFiltersToRequest = (
  filters?: Record<string, unknown> | null,
): Record<string, object> => {
  if (!filters) return {};

  const { spaceFilters, packageFilters, ...alreadyMapped } = filters;
  const request = { ...alreadyMapped } as Record<string, object>;

  if (spaceFilters !== undefined && spaceFilters !== null) {
    request.spaces = spaceFilters as object;
  }
  if (packageFilters !== undefined && packageFilters !== null) {
    request.packages = packageFilters as object;
  }

  return request;
};

/**
 * A saved line carries its `id`, so the backend keeps that line — and its Drive folder — rather than matching it by
 * space and dates, which swaps same-package twins and gives a moved line a new, empty folder.
 */
export const mapFormSpaceBookingsToRequest = (
  bookings: SpaceBookingType[],
): (MediaPlanBookingRequest & { id?: string })[] =>
  bookings.map(
    ({ id, availability, spaceId, status, purchasedServices, paymentTerms }: SpaceBookingType) => ({
      ...(id ? { id } : {}),
      availability: {
        start: !availability?.start?.includes('T')
          ? `${availability.start}T00:00:00Z`
          : availability.start,
        end: !availability?.end?.includes('T') ? `${availability.end}T20:59:59Z` : availability.end,
      },
      spaceId,
      status: status as MediaPlanBookingRequest['status'],
      paymentTerms: paymentTerms ?? 0,
      purchasedServices: (purchasedServices as MediaPlanBookingRequest['purchasedServices']) ?? [],
    }),
  );

export const mapFormPackageBookingsToRequest = (
  packageBookings: PackageBookingType[],
): (MediaPlanPackageBookingRequest & { id?: string })[] =>
  packageBookings.map(
    ({
      id,
      availability,
      advertisingPackageId,
      status,
      paymentTerms,
      bookings,
      purchasedServices,
    }: PackageBookingType) => ({
      ...(id ? { id } : {}),
      availability: {
        start: !availability?.start?.includes('T')
          ? `${availability.start}T00:00:00Z`
          : availability.start,
        end: !availability?.end?.includes('T') ? `${availability.end}T20:59:59Z` : availability.end,
      },
      advertisingPackageId,
      status: status as MediaPlanPackageBookingRequest['status'],
      paymentTerms: paymentTerms ?? 0,
      purchasedServices: (purchasedServices as MediaPlanBookingRequest['purchasedServices']) ?? [],
      bookings: mapFormSpaceBookingsToRequest(bookings),
    }),
  );

export const calculateCampaignDuration = (
  bookings: SpaceBookingType[],
  packageBookings: PackageBookingType[],
  excludeDraft = false,
): { startDate: string; endDate: string } => {
  let allBookings = [...bookings, ...packageBookings.flatMap((pb) => pb.bookings || [])];

  if (excludeDraft) {
    allBookings = allBookings.filter((b) => b.status !== 'DRAFT');
  }

  if (allBookings.length === 0) {
    return { startDate: '', endDate: '' };
  }

  const startDate = allBookings.reduce((min, booking) => {
    const bookingStart = booking.availability.start;
    return !min || bookingStart < min ? bookingStart : min;
  }, '');

  const endDate = allBookings.reduce((max, booking) => {
    const bookingEnd = booking.availability.end;
    return !max || bookingEnd > max ? bookingEnd : max;
  }, '');

  return { startDate, endDate };
};
